import { ActionIcon, Button, Checkbox, Modal, Title, rem } from "@mantine/core";
import IconDots from "@src/assets/icons/tabler/dots.svg?react";
import IconEye from "@src/assets/icons/tabler/eye.svg?react";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import IconUserEdit from "@src/assets/icons/tabler/user-edit.svg?react";
import IconUsers from "@src/assets/icons/tabler/users.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { useArchiveWhiteboard } from "@src/components/board/mutations/archive-whiteboard";
import { useNavbarFavourite } from "@src/components/board/mutations/favourite-navbar-item";
import { useLeaveCard } from "@src/components/board/mutations/leave-card";
import { useLeaveWhiteboard } from "@src/components/board/mutations/leave-whiteboard";
import { useMoveRanklistItem } from "@src/components/board/mutations/move-item";
import { useNavbarRemoveCard } from "@src/components/board/mutations/remove-card-navbar-item";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
	navbarTreeQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import {
	type FlatTreeRow,
	useFlatNavbarTree,
} from "@src/components/board/queries/use-flat-navbar-tree";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import {
	NavBarItemType,
	NavbarSection,
	WhiteboardPermission,
} from "@src/gql/graphql";
import {
	showErrorNotification,
	showSuccessNotification,
} from "@src/utils/notifications";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { getTreeIndentPx } from "./tree-indent";

type FlatBoardRow = FlatTreeRow;

const DELETE_BLOCKED_TOOLTIP = "Published or shared boards can't be deleted";
const DELETE_CONCURRENCY = 3;

function FolderIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="shrink-0 text-text-secondary"
		>
			<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
		</svg>
	);
}

function FolderInputIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1" />
			<path d="M2 13h10" />
			<path d="m9 16 3-3-3-3" />
		</svg>
	);
}

function StarIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
		</svg>
	);
}

function CircleXIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<path d="m15 9-6 6" />
			<path d="m9 9 6 6" />
		</svg>
	);
}

interface MoveToFolderPopoverProps {
	folders: FlatBoardRow[];
	onSelect: (folderKey: string) => void;
	onClose: () => void;
}

function MoveToFolderPopover({
	folders,
	onSelect,
	onClose,
}: MoveToFolderPopoverProps) {
	return (
		<div className="absolute bottom-full right-0 z-50 mb-1 min-w-48 rounded-lg border border-border-subtle bg-surface-default p-2 shadow-lg">
			<div className="mb-1 px-2 py-1 text-xs font-medium text-text-secondary">
				Move to folder
			</div>
			{folders.map((f) => (
				<button
					key={f.key}
					type="button"
					className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-hover-subtle"
					onClick={() => {
						onSelect(f.key);
						onClose();
					}}
				>
					<FolderIcon />
					<span className="truncate">{f.name}</span>
				</button>
			))}
			{folders.length === 0 && (
				<div className="px-2 py-1.5 text-xs text-text-tertiary">
					No folders available
				</div>
			)}
		</div>
	);
}

interface MoreMenuPopoverProps {
	onFavourite: () => void;
	onClose: () => void;
	disabled?: boolean;
}

function MoreMenuPopover({
	onFavourite,
	onClose,
	disabled,
}: MoreMenuPopoverProps) {
	return (
		<div className="absolute bottom-full right-0 z-50 mb-1 min-w-48 rounded-lg border border-border-subtle bg-surface-default p-2 shadow-lg">
			<button
				type="button"
				className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-hover-subtle disabled:cursor-not-allowed disabled:opacity-50"
				onClick={() => {
					if (!disabled) {
						onFavourite();
						onClose();
					}
				}}
				disabled={disabled}
			>
				<StarIcon />
				<span className="truncate">Add to Favorites</span>
			</button>
		</div>
	);
}

interface ManageBoardsModalProps {
	opened: boolean;
	onClose: () => void;
	/** Which navbar ranklist to manage; defaults to My Boards. */
	section?: NavbarSection;
}

export function ManageBoardsModal({
	opened,
	onClose,
	section = NavbarSection.MyWhiteboards,
}: ManageBoardsModalProps) {
	const flatTreeOptions = useMemo(() => ({ enabled: opened }), [opened]);
	const rows = useFlatNavbarTree(section, flatTreeOptions);
	const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
	const [isProcessing, setIsProcessing] = useState(false);
	const [showMovePopover, setShowMovePopover] = useState(false);
	const [showMoreMenu, setShowMoreMenu] = useState(false);

	const { mutateAsync: archiveWhiteboard } = useArchiveWhiteboard(false);
	const { mutateAsync: removeNavbarCard } = useNavbarRemoveCard(false);
	const { mutateAsync: leaveWhiteboard } = useLeaveWhiteboard(false);
	const { mutateAsync: leaveCard } = useLeaveCard(false);
	const { mutateAsync: moveItem } = useMoveRanklistItem();
	const { mutateAsync: favourite } = useNavbarFavourite();
	const queryClient = useQueryClient();

	const isSharedWithMe = section === NavbarSection.SharedWithMe;
	const boardCount = rows.filter((r) => r.type === NavBarItemType.Board).length;
	const folders = rows.filter((r) => r.type === NavBarItemType.Folder);

	const selectedBoardKeys = useMemo(() => {
		return [...selectedKeys].filter((key) => {
			const row = rows.find((r) => r.key === key);
			return row?.type === NavBarItemType.Board;
		});
	}, [selectedKeys, rows]);

	const selectedCardKeys = useMemo(() => {
		return [...selectedKeys].filter((key) => {
			const row = rows.find((r) => r.key === key);
			return row?.type === NavBarItemType.Card;
		});
	}, [selectedKeys, rows]);

	const selectionHasBlockedBoards = useMemo(() => {
		return selectedBoardKeys.some((key) => {
			const row = rows.find((r) => r.key === key);
			return row?.isSharedOrPublished === true;
		});
	}, [selectedBoardKeys, rows]);

	const hasBoardOrCardSelection =
		selectedBoardKeys.length > 0 || selectedCardKeys.length > 0;

	const canDelete =
		!isProcessing && !selectionHasBlockedBoards && hasBoardOrCardSelection;

	const canLeave = !isProcessing && hasBoardOrCardSelection;

	const toggleSelect = useCallback((key: string) => {
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedKeys(new Set());
	}, []);

	const handleDelete = useCallback(async () => {
		const boardKeys = selectedBoardKeys.filter((key) => {
			const row = rows.find((r) => r.key === key);
			return row && !row.isSharedOrPublished;
		});
		const cardKeys = selectedCardKeys;
		if (boardKeys.length === 0 && cardKeys.length === 0) return;

		setIsProcessing(true);
		try {
			for (let i = 0; i < boardKeys.length; i += DELETE_CONCURRENCY) {
				const batch = boardKeys.slice(i, i + DELETE_CONCURRENCY);
				await Promise.all(batch.map((key) => archiveWhiteboard(key)));
			}
			for (let i = 0; i < cardKeys.length; i += DELETE_CONCURRENCY) {
				const batch = cardKeys.slice(i, i + DELETE_CONCURRENCY);
				await Promise.all(
					batch.map((key) => removeNavbarCard({ section, cardId: key })),
				);
			}

			const parts: string[] = [];
			if (boardKeys.length > 0) {
				parts.push(`${boardKeys.length} board(s) deleted`);
			}
			if (cardKeys.length > 0) {
				parts.push(`${cardKeys.length} card(s) removed from workspace`);
			}
			showSuccessNotification("Success", parts.join("; "));
			setSelectedKeys(new Set());
			queryClient.invalidateQueries({ queryKey: navbarBranchQueryKeys.all() });
			queryClient.invalidateQueries({
				queryKey: navbarTreeQueryKeys.tree(section),
			});
		} catch {
			showErrorNotification("Error", "Failed to delete some items");
		} finally {
			setIsProcessing(false);
		}
	}, [
		selectedBoardKeys,
		selectedCardKeys,
		rows,
		archiveWhiteboard,
		removeNavbarCard,
		queryClient,
		section,
	]);

	const handleLeave = useCallback(async () => {
		const boardKeys = selectedBoardKeys;
		const cardKeys = selectedCardKeys;
		if (boardKeys.length === 0 && cardKeys.length === 0) return;

		setIsProcessing(true);
		try {
			for (let i = 0; i < boardKeys.length; i += DELETE_CONCURRENCY) {
				const batch = boardKeys.slice(i, i + DELETE_CONCURRENCY);
				await Promise.all(batch.map((key) => leaveWhiteboard(key)));
			}
			for (let i = 0; i < cardKeys.length; i += DELETE_CONCURRENCY) {
				const batch = cardKeys.slice(i, i + DELETE_CONCURRENCY);
				await Promise.all(batch.map((key) => leaveCard(key)));
			}

			const parts: string[] = [];
			if (boardKeys.length > 0) {
				parts.push(`${boardKeys.length} board(s) left`);
			}
			if (cardKeys.length > 0) {
				parts.push(`${cardKeys.length} card(s) left`);
			}
			showSuccessNotification("Success", parts.join("; "));
			setSelectedKeys(new Set());
			queryClient.invalidateQueries({ queryKey: navbarBranchQueryKeys.all() });
			queryClient.invalidateQueries({
				queryKey: navbarTreeQueryKeys.tree(section),
			});
		} catch {
			showErrorNotification("Error", "Failed to leave some items");
		} finally {
			setIsProcessing(false);
		}
	}, [
		selectedBoardKeys,
		selectedCardKeys,
		leaveWhiteboard,
		leaveCard,
		queryClient,
		section,
	]);

	const handleMoveToFolder = useCallback(
		async (folderKey: string) => {
			const itemKeys = rows
				.filter((r) => selectedKeys.has(r.key))
				.map((r) => r.key);
			if (itemKeys.length === 0) return;

			// Prevent moving a folder into one of its own children
			const selectedFolders = rows.filter(
				(r) => selectedKeys.has(r.key) && r.type === NavBarItemType.Folder,
			);
			for (const folder of selectedFolders) {
				const descendantKeys = rows
					.filter((r) => r.parentKey === folder.key)
					.map((r) => r.key);
				if (descendantKeys.includes(folderKey)) {
					showErrorNotification(
						"Error",
						"Cannot move a folder into its own child folder",
					);
					return;
				}
			}

			setIsProcessing(true);
			try {
				const targetItems =
					queryClient.getQueryData<NavbarBranchItem[]>(
						navbarBranchQueryKeys.branch(section, folderKey),
					) ?? [];

				let afterKey: string | null =
					targetItems[targetItems.length - 1]?.key ?? null;

				const items = itemKeys.map((key) => {
					const row = rows.find((r) => r.key === key);
					const spec = {
						itemKey: key,
						parentKey: folderKey,
						sourceParentKey: row?.parentKey,
						afterKey,
					};
					afterKey = key;
					return spec;
				});

				await moveItem({ section, items });

				const folder = folders.find((f) => f.key === folderKey);
				showSuccessNotification(
					"Successfully moved",
					`${itemKeys.length} item(s) moved to "${folder?.name ?? "folder"}"`,
				);
				setSelectedKeys(new Set());
			} catch {
				showErrorNotification("Error", "Failed to move items");
			} finally {
				setIsProcessing(false);
			}
		},
		[selectedKeys, rows, moveItem, section, queryClient, folders],
	);

	const handleFavourite = useCallback(async () => {
		if (selectedBoardKeys.length === 0) return;

		setIsProcessing(true);
		try {
			await favourite({ section, itemKeys: selectedBoardKeys });
			showSuccessNotification(
				"Success",
				`${selectedBoardKeys.length} board(s) added to favorites`,
			);
			setSelectedKeys(new Set());
		} catch {
			showErrorNotification("Error", "Failed to favorite some boards");
		} finally {
			setIsProcessing(false);
		}
	}, [selectedBoardKeys, favourite, section]);

	const handleClose = () => {
		setSelectedKeys(new Set());
		setShowMovePopover(false);
		setShowMoreMenu(false);
		onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			radius="md"
			size="md"
			padding={rem(24)}
			withCloseButton={false}
			centered
			zIndex={2000}
		>
			<div className="mb-4 flex items-center justify-between">
				<Title order={4}>
					{section === NavbarSection.SharedWithMe
						? "Manage shared workspace"
						: "Manage workspace"}
				</Title>
				<ActionIcon
					variant="subtle"
					color="var(--ui-color-text-primary)"
					size="sm"
					onClick={handleClose}
					aria-label="Close manage workspace modal"
					title="Close"
				>
					<IconX className="h-4 w-4" />
				</ActionIcon>
			</div>

			<div className="mb-3 flex items-center justify-between text-sm text-text-primary">
				<div className="flex items-center gap-1">
					<span>
						{boardCount} {boardCount === 1 ? "board" : "boards"}
					</span>
					{selectedKeys.size > 0 && (
						<>
							<span className="mx-0.5 text-text-tertiary">/</span>
							<span className="text-brand-primary">
								{selectedKeys.size} selected
							</span>
							<button
								type="button"
								onClick={clearSelection}
								className="text-text-tertiary hover:text-text-secondary"
								aria-label="Clear selection"
							>
								<CircleXIcon />
							</button>
						</>
					)}
				</div>
			</div>

			<div className="flex flex-col gap-0.5 max-h-80 overflow-y-auto">
				{rows.map((row) => {
					const isFolder = row.type === NavBarItemType.Folder;
					return (
						<button
							key={row.key}
							type="button"
							style={{ paddingLeft: getTreeIndentPx(row.depth) }}
							className={clsx(
								"flex items-center gap-2 py-1.5 pr-2 rounded-md cursor-pointer text-sm w-full text-left",
								"hover:bg-surface-hover-subtle",
								selectedKeys.has(row.key) && "bg-interaction-selected",
							)}
							onClick={() => toggleSelect(row.key)}
						>
							<Checkbox.Indicator
								aria-hidden
								checked={selectedKeys.has(row.key)}
								size="xs"
								color="var(--ui-color-brand-primary)"
								className="shrink-0"
							/>
							{isFolder && <FolderIcon />}
							<span
								className={clsx(
									"truncate flex-1",
									isFolder && "font-medium text-text-secondary",
								)}
							>
								{row.name}
							</span>
							{!isFolder &&
								row.sharedWithMeListItemAccess != null &&
								(row.sharedWithMeListItemAccess ===
								WhiteboardPermission.Write ? (
									<IconUserEdit
										width={14}
										height={14}
										className="mr-1 shrink-0 text-text-tertiary"
										aria-hidden
									/>
								) : (
									<IconEye
										width={14}
										height={14}
										className="mr-1 shrink-0 text-text-tertiary"
										aria-hidden
									/>
								))}
							{!isFolder &&
								row.sharedWithMeListItemAccess == null &&
								row.isSharedOrPublished && (
									<IconUsers
										width={14}
										height={14}
										className="mr-1 shrink-0 text-text-tertiary"
										aria-hidden
									/>
								)}
						</button>
					);
				})}
			</div>

			<div className="flex items-center gap-2 mt-4 relative">
				<Button
					variant="subtle"
					size="xs"
					color="var(--ui-color-text-primary)"
					onClick={handleClose}
				>
					Cancel
				</Button>
				<div className="flex-1" />
				{isSharedWithMe ? (
					<Button
						variant="subtle"
						color="var(--ui-color-danger)"
						size="xs"
						leftSection={<IconTrash width={14} height={14} />}
						disabled={!canLeave}
						loading={isProcessing}
						onClick={handleLeave}
					>
						Leave
					</Button>
				) : (
					<Tooltip
						label={DELETE_BLOCKED_TOOLTIP}
						disabled={!selectionHasBlockedBoards}
						withArrow
					>
						<span className="inline-flex">
							<Button
								variant="subtle"
								color="var(--ui-color-danger)"
								size="xs"
								leftSection={<IconTrash width={14} height={14} />}
								disabled={!canDelete}
								loading={isProcessing}
								onClick={handleDelete}
							>
								Delete
							</Button>
						</span>
					</Tooltip>
				)}
				<div className="relative">
					<Button
						variant="subtle"
						color="var(--ui-color-brand-primary)"
						size="xs"
						leftSection={<FolderInputIcon />}
						disabled={selectedKeys.size === 0 || isProcessing}
						onClick={() => setShowMovePopover((v) => !v)}
					>
						Move to folder…
					</Button>
					{showMovePopover && (
						<MoveToFolderPopover
							folders={folders.filter((f) => !selectedKeys.has(f.key))}
							onSelect={handleMoveToFolder}
							onClose={() => setShowMovePopover(false)}
						/>
					)}
				</div>
				<div className="relative">
					<Button
						variant="subtle"
						color="var(--ui-color-brand-primary)"
						size="xs"
						disabled={selectedKeys.size === 0 || isProcessing}
						onClick={() => setShowMoreMenu((v) => !v)}
					>
						<IconDots width={14} height={14} />
					</Button>
					{showMoreMenu && (
						<MoreMenuPopover
							onFavourite={handleFavourite}
							onClose={() => setShowMoreMenu(false)}
							disabled={selectedBoardKeys.length === 0 || isProcessing}
						/>
					)}
				</div>
			</div>
		</Modal>
	);
}
